import { useState, useRef, useEffect } from 'react'
import { Upload, Download, X, Image as ImageIcon } from 'lucide-react'
import Card from '../components/Card'
import Button from '../components/Button'

export default function ImageEditor() {
  const [selectedFile, setSelectedFile] = useState(null)
  const [previewUrl, setPreviewUrl] = useState(null)
  const [editedImage, setEditedImage] = useState(null)
  const [brightness, setBrightness] = useState(100)
  const [contrast, setContrast] = useState(100)
  const [saturation, setSaturation] = useState(100)
  const [blur, setBlur] = useState(0)
  const [hue, setHue] = useState(0)
  const fileInputRef = useRef(null)

  const handleFileSelect = (file) => {
    if (!file.type.startsWith('image/')) {
      alert('Please select a valid image file.')
      return
    }
    setSelectedFile(file)
    setEditedImage(null)
    // Create preview URL
    const url = URL.createObjectURL(file)
    setPreviewUrl(url)
    // Reset filters
    setBrightness(100)
    setContrast(100)
    setSaturation(100)
    setBlur(0)
    setHue(0)
  }

  const applyFilters = () => {
    if (!selectedFile) return

    const img = new Image()
    const reader = new FileReader()

    reader.onload = (e) => {
      img.onload = () => {
        const canvas = document.createElement('canvas')
        canvas.width = img.width
        canvas.height = img.height
        const ctx = canvas.getContext('2d')

        ctx.drawImage(img, 0, 0)

        // Apply CSS filters (for preview)
        const filterString = `
          brightness(${brightness}%)
          contrast(${contrast}%)
          saturate(${saturation}%)
          blur(${blur}px)
          hue-rotate(${hue}deg)
        `.trim()

        // For actual image processing, we need to use canvas manipulation
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
        const data = imageData.data

        // Apply brightness and contrast
        const brightnessFactor = brightness / 100
        const contrastFactor = (contrast / 100 - 1) * 255

        for (let i = 0; i < data.length; i += 4) {
          // Brightness
          data[i] = Math.min(255, Math.max(0, data[i] * brightnessFactor))
          data[i + 1] = Math.min(255, Math.max(0, data[i + 1] * brightnessFactor))
          data[i + 2] = Math.min(255, Math.max(0, data[i + 2] * brightnessFactor))

          // Contrast
          data[i] = Math.min(255, Math.max(0, data[i] + contrastFactor))
          data[i + 1] = Math.min(255, Math.max(0, data[i + 1] + contrastFactor))
          data[i + 2] = Math.min(255, Math.max(0, data[i + 2] + contrastFactor))
        }

        ctx.putImageData(imageData, 0, 0)

        // Note: Saturation, blur, and hue rotation are complex and would require
        // more advanced image processing. For now, we'll apply them via CSS for preview
        // and use a simpler canvas approach for the download

        canvas.toBlob(
          (blob) => {
            const url = URL.createObjectURL(blob)
            setEditedImage({ url, blob, filter: filterString })
          },
          selectedFile.type,
          0.95
        )
      }
      img.src = e.target.result
    }

    reader.readAsDataURL(selectedFile)
  }

  const downloadImage = () => {
    if (!editedImage) return

    const link = document.createElement('a')
    link.href = editedImage.url
    link.download = `edited-${selectedFile.name}`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const resetFilters = () => {
    setBrightness(100)
    setContrast(100)
    setSaturation(100)
    setBlur(0)
    setHue(0)
    setEditedImage(null)
  }

  const clearFile = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
    }
    setSelectedFile(null)
    setPreviewUrl(null)
    setEditedImage(null)
    resetFilters()
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  // Generate CSS filter string for real-time preview
  const getFilterString = () => {
    return `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%) blur(${blur}px) hue-rotate(${hue}deg)`
  }

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
      }
    }
  }, [previewUrl])

  return (
    <div className="space-y-6">
      <Card
        className="cursor-pointer border-gray-300 hover:border-primary-400"
        onClick={() => fileInputRef.current?.click()}
      >
        <Card.Body className="text-center py-8">
          {!selectedFile ? (
            <div>
              <ImageIcon className="w-12 h-12 text-indigo-600 mx-auto mb-4" />
              <p className="text-base font-semibold text-gray-900">Click to upload image</p>
              <p className="text-sm text-gray-500 mt-1">Select an image to edit</p>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-4">
              <div className="text-left">
                <p className="text-base font-semibold text-gray-900">{selectedFile.name}</p>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  clearFile()
                }}
                className="ml-auto p-2 hover:bg-gray-200 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          )}
        </Card.Body>
      </Card>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.[0]) handleFileSelect(e.target.files[0])
        }}
      />

      {selectedFile && (
        <>
          {/* Real-time Preview */}
          <Card>
            <Card.Body>
              <div className="text-center space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Preview</h3>
                <div className="inline-block p-4 bg-gray-100 rounded-lg">
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="max-w-full h-auto max-h-96"
                    style={{ filter: getFilterString() }}
                  />
                </div>
              </div>
            </Card.Body>
          </Card>

          <Card>
            <Card.Body>
              <div className="space-y-6">
                <div>
                  <label className="label">Brightness: {brightness}%</label>
                  <input
                    type="range"
                    min="0"
                    max="200"
                    value={brightness}
                    onChange={(e) => setBrightness(Number(e.target.value))}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="label">Contrast: {contrast}%</label>
                  <input
                    type="range"
                    min="0"
                    max="200"
                    value={contrast}
                    onChange={(e) => setContrast(Number(e.target.value))}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="label">Saturation: {saturation}%</label>
                  <input
                    type="range"
                    min="0"
                    max="200"
                    value={saturation}
                    onChange={(e) => setSaturation(Number(e.target.value))}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="label">Blur: {blur}px</label>
                  <input
                    type="range"
                    min="0"
                    max="20"
                    value={blur}
                    onChange={(e) => setBlur(Number(e.target.value))}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="label">Hue: {hue}°</label>
                  <input
                    type="range"
                    min="-180"
                    max="180"
                    value={hue}
                    onChange={(e) => setHue(Number(e.target.value))}
                    className="w-full"
                  />
                </div>

                <div className="flex gap-4">
                  <Button onClick={applyFilters} variant="primary" className="flex-1">
                    Process & Download
                  </Button>
                  <Button onClick={resetFilters} variant="outline">
                    Reset
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>

          {editedImage && (
            <Card>
              <Card.Body>
                <div className="text-center space-y-4">
                  <p className="text-sm text-gray-600">Processed image ready for download</p>
                  <Button onClick={downloadImage} variant="primary" size="lg" className="w-full">
                    <Download className="w-5 h-5 mr-2" />
                    Download Edited Image
                  </Button>
                </div>
              </Card.Body>
            </Card>
          )}
        </>
      )}
    </div>
  )
}

